import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import ModalLayout from "./ModalLayout";
import MUIDataTable from "mui-datatables";
import { useNavigate } from "react-router";
import { useEffect, useState } from "react";
import { alpha } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';
import EditIcon from "@mui/icons-material/Edit";
import { IconCalendarPlus } from "@tabler/icons";
import { configState } from "../../../recoil/atoms";
import PeopleAlt from "@mui/icons-material/PeopleAlt";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import HighlightOff from "@mui/icons-material/HighlightOff";
import AddPatientToGroupModal from "./AddPatientToGroupModal";
import MainCard from "../../../styles/ui-component/cards/MainCard";
import { PatientApi, GroupsApi } from "../../../api/medical-operations";
import useAuthorization from "../../Authentication/permissions/Authorization";
import RemoveConfirmDialog from "../../../utils/FormFields/RemoveConfirmDialog";
import {Button, CircularProgress, Grid, Box, Tooltip, Typography, Modal} from "@mui/material";
import {GetPatientsResponseBasicPatients} from "../../../api/medical-operations/models/get-patients-response-basic-patients";

interface LocationState {
  groupID: string;
  groupName: string;
}

const PatientGroup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const config = useRecoilValue(configState);
  const groupName = location.state ? (location.state as LocationState).groupName : '';

  const [rowID, setRowID] = useState<string>("");
  const [patientName, setPatientName] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [patients, setPatients] = useState<GetPatientsResponseBasicPatients[]>([]);

  const { canEditTenantPatient, canScheduleAppointment, canAddPatientToGroups, canGetGroupMembers, canDeletePatientFromGroup } = useAuthorization();

  const handleOpenModal = () => setOpenModal(true);  

  const getMembers = () => {
    setIsLoading(true);
    const patient = new GroupsApi(config);
    patient
      .getMembersOfGroup(groupName)
      .then((res) => {
        setPatients(res.data.patients!);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };  
  
  useEffect(() => {        
    getMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps    
  },[]); 

  const deletePatient = (id: string) => {
    const Patient = new PatientApi(config);
    Patient.deletePatientFromGroup(id,groupName)
      .then((res) => {
        getMembers();
        toast.success(res?.data?.Message!);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      });
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    getMembers();
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleDelete = (id: string) => {
    setRowID(id);
    handleOpenDialog();
  };

  const handleConfirm = () => {
    deletePatient(rowID);
    handleCloseDialog();
  };

  const columns = [
    {
      name: "PatientID",
      options: {
        display: false,
        filter: false,
        print: false,
        download: false,
      },
    },             
    {
      name: "FirstName",
      label: "First Name",
      options: {
        filter: true,
      },
    },
    {
      name: "MiddleName",
      label: "Middle Name",
      options: {
        filter: true,
      },
    },
    {
      name: "FamilyName",
      label: "Family Name",
      options: {
        filter: true,
      },
    },
    {
      name: "action",
      label: "Action",
      options: {
        filter: false,
        sort: false,
        print: false,
        download: false,
        setCellHeaderProps: () => ({ align: "center" }),
        setCellProps: () => ({ align: "center" }),
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => (
          <>
            {canEditTenantPatient && (
              <Tooltip title="Edit Patient Info">
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => {                                       
                    navigate(`/patient/${tableMeta.rowData[0]}/user`, {
                      state: tableMeta.tableData[tableMeta.rowIndex],                                            
                    });                                                                                                                                                        
                  }}
                >
                  <EditIcon color="secondary" />
                </Button>                
              </Tooltip>
            )}                     
            {canScheduleAppointment && (
              <Tooltip title="Add Appointment">
                <Button
                  variant="outlined"
                  color="error"
                  sx={{
                    ml: { xs: 0, xl: 2 },                    
                    borderColor: alpha('#8e44ad', 0.7),
                    '&:hover': {
                      borderColor: '#8e44ad',
                    },
                  }}
                  onClick={() => {
                    navigate(`/add-appointment/${tableMeta.rowData[0]}`, {
                      state: tableMeta.tableData[tableMeta.rowIndex]
                    });
                  }}                  
                >
                  <IconCalendarPlus color="purple" />
                </Button>
              </Tooltip>
            )} 
            {canDeletePatientFromGroup && (
              <Tooltip title="Remove Patient From Group">
                <Button
                  variant="outlined"
                  color="error"
                  sx={{
                    ml: { xs: 0, xl: 2 },
                  }}
                  onClick={() => {
                    const firstName = tableMeta.rowData[1];
                    const lastName = tableMeta.rowData[3];
                    const fullName = `${firstName} ${lastName}`;

                    handleDelete(tableMeta.rowData[0]);
                    setPatientName(fullName);
                  }}
                >
                  <HighlightOff color="error" />
                </Button>
              </Tooltip>
            )}              
          </>
        ),
      },
    },
  ];

  return (
    <>
      {canGetGroupMembers && (
        <MainCard>
          <Grid>
              <> 
                <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <Typography variant="h3" sx={{ color: 'secondary', ml: 3 }}>
                    <PeopleAlt fontSize="large" />
                  </Typography>
                  <Typography variant="h3" sx={{  mt: 0, ml: 2 }}>
                    {groupName}
                  </Typography> 
                </Box>   
                <Modal
                  open={openModal}
                  onClose={handleCloseModal}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                  <Box>
                    <ModalLayout>
                      <AddPatientToGroupModal handleCloseModal={handleCloseModal} />
                    </ModalLayout>
                  </Box>
                </Modal>                           
                <MUIDataTable
                  data={isLoading ? [] : patients}                              
                  columns={columns}
                  options={{                                    
                    filter: true,
                    viewColumns: false,
                    selectableRows: "none",
                    selectableRowsOnClick: false,
                    elevation: 0,
                    rowsPerPageOptions : [10,30,50,100],                    
                    filterType: "dropdown",                    
                    enableNestedDataAccess: ".",
                    responsive: "standard",
                    jumpToPage: false,
                    textLabels: {
                      body: {
                        noMatch: isLoading ? <CircularProgress/> : 'This group is empty'
                      }
                    },                                                                                                                                                       
                    customToolbar: () => {
                      return (
                        canAddPatientToGroups && (
                          <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<PersonAddIcon />}                            
                            onClick={handleOpenModal}
                            sx={{
                              ml: "50px",
                            }}
                          >
                            Add Patient To Group
                          </Button>
                        )
                      );
                    },
                  }}
                  title=""
                />                
                <RemoveConfirmDialog
                  name={"patient"}
                  record={patientName}
                  group={groupName}
                  handleCloseDialog={handleCloseDialog}
                  handleConfirm={handleConfirm}
                  openDialog={openDialog}
                />
              </>
          </Grid>
        </MainCard>
      )}
    </>
  );
};

export default PatientGroup;
